/**
 * src/components/mobile/MobileDashboard.jsx
 * Route: /mobile
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import MobileLayout from './MobileLayout';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

const STATUS_CFG = {
  operational: { label: 'Operational', color: '#16a34a' },
  maintenance:  { label: 'Maintenance', color: '#f59e0b' },
  breakdown:    { label: 'Breakdown',   color: '#dc2626' },
  retired:      { label: 'Retired',     color: '#94a3b8' },
};

export default function MobileDashboard() {
  const navigate = useNavigate();
  const location = useLocation();   // triggers re-fetch on every visit to /mobile
  const [stats, setStats]   = useState({ operational:0, maintenance:0, breakdown:0, retired:0, total:0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/assets/stats/status`, { headers: auth() })
      .then(r => {
        const d = r.data || {};
        setStats({
          operational: d.operational || 0,
          maintenance:  d.maintenance  || 0,
          breakdown:    d.breakdown    || 0,
          retired:      d.retired      || 0,
          total:        d.total        || Object.values(d).reduce((s,v)=>s+(+v||0),0),
        });
      })
      .catch(() => {
        // Fallback: fetch ALL assets and count statuses (limit raised to 5000)
        axios.get(`${API}/assets/?limit=5000`, { headers: auth() })
          .then(r => {
            const assets = r.data?.data || r.data || [];
            const counts = { operational:0, maintenance:0, breakdown:0, retired:0 };
            assets.forEach(a => {
              const s = (a.status || a.current_status || 'operational').toLowerCase();
              if (s.includes('maintenance'))    counts.maintenance++;
              else if (s.includes('breakdown')) counts.breakdown++;
              else if (s.includes('retired'))   counts.retired++;
              else                              counts.operational++;
            });
            setStats({ ...counts, total: assets.length });
          }).catch(()=>{});
      })
      .finally(() => setLoading(false));
  }, [location.key]);   // re-fetch every time user navigates to /mobile

  /* Date string */
  const today = new Date().toLocaleDateString('en-GB', {
    weekday:'long', day:'numeric', month:'long', year:'numeric'
  });

  /* Donut chart via conic-gradient */
  const total = stats.total || 1;
  const segments = [
    { key:'operational', ...STATUS_CFG.operational, val: stats.operational },
    { key:'maintenance',  ...STATUS_CFG.maintenance,  val: stats.maintenance  },
    { key:'breakdown',    ...STATUS_CFG.breakdown,    val: stats.breakdown    },
    { key:'retired',      ...STATUS_CFG.retired,      val: stats.retired      },
  ];
  let cum = 0;
  const gradient = segments.map(s => {
    const pct = (s.val / total) * 100;
    const from = cum.toFixed(1); cum += pct;
    return `${s.color} ${from}% ${cum.toFixed(1)}%`;
  }).join(', ');

  return (
    <MobileLayout active="home">
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Date + QR Scan */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>{today}</span>
          <button onClick={() => navigate('/mobile/scan')}
            style={{ background: '#1a56db', color: '#fff', border: 'none',
              borderRadius: 8, padding: '8px 14px', fontSize: 12,
              fontWeight: 700, cursor: 'pointer', display: 'flex',
              alignItems: 'center', gap: 6 }}>
            📷 Scan Asset QR Code
          </button>
        </div>

        {/* Asset Health Status card */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '20px 16px',
          boxShadow: '0 1px 6px rgba(0,0,0,.06)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1f2e',
            textAlign: 'center', marginBottom: 20 }}>
            Asset Health Status
          </div>

          {/* Donut */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <div style={{ position: 'relative', width: 150, height: 150 }}>
              <div style={{
                width: 150, height: 150, borderRadius: '50%',
                background: loading
                  ? '#f1f5f9'
                  : `conic-gradient(${gradient})`,
              }}/>
              {/* Hole */}
              <div style={{ position: 'absolute', inset: 30, borderRadius: '50%',
                background: '#fff', display: 'flex', alignItems: 'center',
                justifyContent: 'center', flexDirection: 'column' }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: '#1a1f2e',
                  lineHeight: 1 }}>
                  {String(stats.total).padStart(2, '0')}
                </span>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {segments.map(s => {
              const pct = stats.total > 0 ? Math.round((s.val / stats.total) * 100) : 0;
              return (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3,
                    background: s.color, flexShrink: 0 }}/>
                  <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>{s.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e',
                    minWidth: 24, textAlign: 'right' }}>{s.val}</span>
                  <span style={{ fontSize: 12, color: '#94a3b8',
                    minWidth: 36, textAlign: 'right' }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick links */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { icon:'🏭', label:'All Assets',    path:'/mobile/assets',      color:'#eff6ff', border:'#bfdbfe' },
            { icon:'🔧', label:'Work Orders',   path:'/mobile/work-orders', color:'#fef3c7', border:'#fde68a' },
            { icon:'⚡', label:'Breakdowns',    path:'/mobile/work-orders?type=breakdown', color:'#fff1f2', border:'#fecaca' },
            { icon:'📋', label:'PM Schedules',  path:'/mobile/work-orders?type=pm',         color:'#f0fdf4', border:'#bbf7d0' },
          ].map(q => (
            <button key={q.label} onClick={() => navigate(q.path)}
              style={{ background: q.color, border: `1px solid ${q.border}`,
                borderRadius: 12, padding: '16px 12px',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                gap: 6, cursor: 'pointer', textAlign: 'left' }}>
              <span style={{ fontSize: 24 }}>{q.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1f2e' }}>{q.label}</span>
            </button>
          ))}
        </div>

      </div>
    </MobileLayout>
  );
}
